import { v2Client } from '@api/apiClient'
import { MessageType } from '@components/chat/types'
import { Pagination } from '@components/utils/pagination'
import { useQuery } from '@tanstack/react-query'
import mime from 'mime'

export const conversationKeys = {
  getAll: () => ['conversations'],
  getConversation: (chat_id: string) => [...conversationKeys.getAll(), 'getConversation', chat_id],
}

export type GetConversation = {
  active_index_id: number
  messages: MessageType[]
  pagination: Pagination
}

export const useGetConversation = (conversationId: string | undefined) => {
  return useQuery({
    enabled: !!conversationId,
    queryKey: conversationId ? conversationKeys.getConversation(conversationId) : [],
    queryFn: () =>
      v2Client.get(`conversations/${conversationId}/messages`).then((res): GetConversation => {
        return {
          messages: res.data.data.reduce((prev, next, index) => {
            prev.push({
              created_at: new Date(next.created_at).toISOString(),
              type: next.type,
              query: next.query,
              order: index * 2,
              documents: next.message_files?.map((file: any) => ({
                file_id: file.id,
                file_uri: file.url,
                filename: file.filename,
                metadata: {
                  filetype: mime.getType(file.filename.split('.').pop() || ''),
                },
                relative_path: file.filename,
              })),
            })
            if (next.answer || next.status === 'error') {
              prev.push({
                type: 'system',
                answer: next.answer ?? '',
                order: index * 2 + 1,
                error: next.error,
                images: next.retriever_resources
                  ?.filter((res) => res.document_name.endsWith('.png'))
                  .map((img: any) => ({
                    position: img.position,
                    title: img.content,
                    url: `/images/${img.document_name}`,
                  })),
                documents: next.retriever_resources
                  ?.filter((res) => !res.document_name.endsWith('.png'))
                  .map((res) => ({
                    file_id: res.document_id,
                    file_uri: `${import.meta.env['VITE_API_URL_V2']}/documents/${res.document_id}/download`,
                    filename: res.document_name,
                    metadata: {
                      filetype: mime.getType(res.document_name.split('.').pop() || ''),
                    },
                    relative_path: res.document_name,
                  }))
                  // Filter out duplicate documents
                  .filter((doc, index, arr) => {
                    return arr.findIndex((d) => d.file_id === doc.file_id) === arr.indexOf(doc)
                  }),
              })
            }
            return prev
          }, []),
          active_index_id: 1,
          pagination: {
            current_page: 1,
            total_pages: 1,
            total_records: res.data.data.length,
            next_page: null,
            prev_page: null,
          },
        }
      }),
  })
}
